import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import TicketsCard from "../components/TicketsCard";
import EventOwnerCard from "../components/EventOwnerCard";
import { useAuth } from "../context/AuthContext";
import firebase, { auth, db } from "../firebase/config";
import {
  getReferralByUserId, getOrdersByReferralCode,
  getReferralWithdrawalsByCode, createReferralWithdrawal,
  getEventsByOwner,
} from "../firebase/firestoreService";
import { MOBILE_OPERATORS } from "../services/paychangu";
import "../styles/myProfile.css";

function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

const fmt  = (n) => `MK ${Number(n).toLocaleString()}`;
const getItemImage = (item) =>
  item.selImage || item.image || item.images?.[0] || item.colors?.[0]?.image || "";
const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const STATUS_STYLE = {
  Pending:   { background: "#fff3e0", color: "#f57c00" },
  Confirmed: { background: "#e3f2fd", color: "#1565c0" },
  Shipped:   { background: "#f3e5f5", color: "#7b1fa2" },
  Delivered: { background: "#e8f5e9", color: "#2e7d32" },
  Cancelled: { background: "#fde8e8", color: "#c0392b" },
};

const WITHDRAWAL_STATUS_STYLE = {
  Pending:  { background: "#fff3e0", color: "#f57c00" },
  Paid:     { background: "#e8f5e9", color: "#2e7d32" },
  Rejected: { background: "#fde8e8", color: "#c0392b" },
};

// ─── Personal Details Card ────────────────────────────────────────────────────
function PersonalCard({ user, isPhoneUser, onSignOut }) {
  const [profile,  setProfile]  = useState(null);
  const [editing,  setEditing]  = useState(false);
  const [draft,    setDraft]    = useState({});
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");

  useEffect(() => {
    if (!user) return;
    if (isPhoneUser) {
      db.collection("users").doc(user.uid).get().then((doc) => {
        if (doc.exists) {
          const d = doc.data();
          setProfile(d);
          setDraft(d);
        }
      });
    } else {
      const parts = (user.displayName || "").split(" ");
      const d = {
        firstName: parts[0] || "",
        lastName:  parts.slice(1).join(" ") || "",
        email:     user.email || "",
        phone:     "",
      };
      setProfile(d);
      setDraft(d);
    }
  }, [user, isPhoneUser]);

  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!draft.firstName.trim() || !draft.lastName.trim()) {
      setError("First and last name are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await db.collection("users").doc(user.uid).update({
        firstName: draft.firstName.trim(),
        lastName:  draft.lastName.trim(),
        email:     draft.email?.trim() || "",
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      await auth.currentUser.updateProfile({
        displayName: `${draft.firstName.trim()} ${draft.lastName.trim()}`,
      });
      setProfile({ ...draft });
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const initials = profile
    ? `${(profile.firstName || "?")[0]}${(profile.lastName || "")[0] || ""}`.toUpperCase()
    : "?";

  return (
    <div className="profile-card">
      <div className="profile-card__header">
        <h2 className="profile-card__title">Personal Details</h2>
        {!editing && isPhoneUser && (
          <button className="profile-card__edit-btn" onClick={() => setEditing(true)}>Edit</button>
        )}
      </div>

      <div className="profile-avatar">{initials}</div>

      {editing ? (
        <div className="profile-form">
          <div className="profile-form__row">
            <div className="profile-form__group">
              <label className="profile-form__label">First Name</label>
              <input className="profile-form__input" value={draft.firstName || ""}
                onChange={(e) => set("firstName", e.target.value)} />
            </div>
            <div className="profile-form__group">
              <label className="profile-form__label">Last Name</label>
              <input className="profile-form__input" value={draft.lastName || ""}
                onChange={(e) => set("lastName", e.target.value)} />
            </div>
          </div>
          <div className="profile-form__group">
            <label className="profile-form__label">Email <span className="profile-form__optional">(optional)</span></label>
            <input className="profile-form__input" type="email" value={draft.email || ""}
              onChange={(e) => set("email", e.target.value)} placeholder="your@email.com" />
          </div>
          <div className="profile-form__group">
            <label className="profile-form__label">Phone</label>
            <input className="profile-form__input" value={draft.phone || ""} disabled />
          </div>
          {error && <p className="profile-form__error">{error}</p>}
          <div className="profile-form__actions">
            <button className="profile-form__cancel" onClick={() => { setEditing(false); setDraft(profile); setError(""); }}>
              Cancel
            </button>
            <button className="profile-form__save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      ) : (
        <div className="profile-details">
          {[
            { label: "First Name", value: profile?.firstName },
            { label: "Last Name",  value: profile?.lastName  },
            { label: "Phone",      value: profile?.phone     },
            { label: "Email",      value: profile?.email     },
          ].map(({ label, value }) => (
            <div key={label} className="profile-details__row">
              <span className="profile-details__label">{label}</span>
              <span className="profile-details__value">{value || <span className="profile-details__empty">—</span>}</span>
            </div>
          ))}
        </div>
      )}

      {!editing && (
        <button className="profile-card__signout-btn" onClick={onSignOut}>
          Sign out
        </button>
      )}
    </div>
  );
}

// ─── Orders Card ──────────────────────────────────────────────────────────────
function OrdersCard({ userId }) {
  const [orders,      setOrders]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const prevReceiptsRef               = useRef({});
  const isFirstLoadRef                = useRef(true);

  useEffect(() => {
    if (!userId) return;

    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    const unsub = db.collection("orders")
      .where("userId", "==", userId)
      .onSnapshot((snap) => {
        const data = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));

        if (!isFirstLoadRef.current) {
          data.forEach((order) => {
            const orderId = order.id;
            if (order.receiptUrl && !prevReceiptsRef.current[orderId]) {
              if (Notification.permission === "granted") {
                new Notification("Your receipt is ready! 📦", {
                  body: `Courier receipt for order #${(order.chargeId || orderId).slice(0, 8).toUpperCase()} is available. Open your profile to view it.`,
                  icon: "/favicon.ico",
                });
              }
            }
            prevReceiptsRef.current[orderId] = order.receiptUrl || null;
          });
        } else {
          data.forEach((order) => {
            prevReceiptsRef.current[order.id] = order.receiptUrl || null;
          });
          isFirstLoadRef.current = false;
        }

        setOrders(data);
        setLoading(false);
      }, (err) => {
        console.error(err);
        setLoading(false);
      });

    return () => unsub();
  }, [userId]);

  return (
    <div className="profile-card profile-card--orders">
      <div className="profile-card__header">
        <h2 className="profile-card__title">Previous Orders</h2>
        {!loading && <span className="profile-card__count">{orders.length} order{orders.length !== 1 ? "s" : ""}</span>}
      </div>

      <div className="profile-orders__whatsapp-notice">
        <span>📦 Courier receipts are sent via WhatsApp once your order is dispatched.</span>
        <a
          href="https://wa.me/265888919178"
          target="_blank"
          rel="noopener noreferrer"
          className="profile-orders__whatsapp-btn"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Enquire
        </a>
      </div>

      {loading ? (
        <div className="profile-orders__loading">
          <div className="profile-orders__spinner" />
          <p>Loading orders…</p>
        </div>
      ) : orders.length === 0 ? (
        <p className="profile-orders__empty">No orders yet.</p>
      ) : (
        <div className="profile-orders__list">
          {orders.map((order) => (
            <div key={order.id} className="profile-order">
              <div className="profile-order__top">
                <div>
                  <p className="profile-order__id">#{order.chargeId || order.id.slice(0, 8).toUpperCase()}</p>
                  <p className="profile-order__date">{fmtDate(order.createdAt)}</p>
                </div>
                <div className="profile-order__right">
                  <span className="profile-order__status"
                    style={STATUS_STYLE[order.status] || STATUS_STYLE.Pending}>
                    {order.status || "Pending"}
                  </span>
                  <p className="profile-order__total">{fmt(order.total)}</p>
                </div>
              </div>

              {order.deliverySummary && (
                <p className="profile-order__delivery">📦 {order.deliverySummary}</p>
              )}

              {!["Delivered", "Cancelled"].includes(order.status) && (
                <p className="profile-order__eta">🕐 Expected delivery: 1–2 days</p>
              )}

              {order.receiptUrl && (
                <div className="profile-order__receipt-row">
                  <button
                    className="profile-order__receipt-btn"
                    onClick={() => setLightboxUrl(order.receiptUrl)}
                  >
                    📄 View Receipt
                  </button>
                  <a
                    href={order.receiptUrl}
                    download={`receipt-${(order.chargeId || order.id).slice(0, 8).toUpperCase()}.jpg`}
                    className="profile-order__receipt-btn profile-order__receipt-btn--download"
                  >
                    ↓ Download
                  </a>
                </div>
              )}

              <div className="profile-order__items">
                {(order.items || []).map((item, i) => (
                  <div key={i} className="profile-order__item">
                    {getItemImage(item) && (
                      <img
                        src={getItemImage(item)}
                        alt={item.name}
                        className="profile-order__thumb"
                      />
                    )}
                    <div className="profile-order__item-info">
                      <p className="profile-order__item-name">{item.name}</p>
                      <p className="profile-order__item-meta">
                        {[item.selColor, item.selSize && `Size ${item.selSize}`].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <p className="profile-order__item-price">{fmt(item.price)}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {lightboxUrl && (
        <div className="profile-receipt-lightbox" onClick={() => setLightboxUrl(null)}>
          <button className="profile-receipt-lightbox__close" onClick={() => setLightboxUrl(null)}>✕</button>
          <img
            src={lightboxUrl}
            alt="Courier receipt"
            className="profile-receipt-lightbox__img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

// ─── Referrals Card ───────────────────────────────────────────────────────────
// preview=true (owner using "View as Referral Holder") shows the design with
// sample data and never touches Firestore — no fetches, no real writes.
function ReferralsCard({ data, preview = false }) {
  const fmtMK = (n) => `MK ${Number(n || 0).toLocaleString()}`;
  const link  = `${window.location.origin}/?ref=${data.code}`;
  const [copied,        setCopied]        = useState(false);
  const [orders,        setOrders]        = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(!preview);

  const [withdrawals,    setWithdrawals]    = useState([]);
  const [wLoading,       setWLoading]       = useState(!preview);
  const [formOpen,       setFormOpen]       = useState(false);
  const [wForm,          setWForm]          = useState({ operator: "airtel", phone: "", amount: "" });
  const [wSubmitting,    setWSubmitting]    = useState(false);
  const [wError,         setWError]         = useState("");

  useEffect(() => {
    if (preview) return;
    getOrdersByReferralCode(data.code)
      .then(setOrders)
      .finally(() => setOrdersLoading(false));
  }, [data.code, preview]);

  const loadWithdrawals = useCallback(() => {
    setWLoading(true);
    getReferralWithdrawalsByCode(data.code)
      .then(setWithdrawals)
      .finally(() => setWLoading(false));
  }, [data.code]);

  useEffect(() => {
    if (preview) return;
    loadWithdrawals();
  }, [preview, loadWithdrawals]);

  const copyLink = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const setW = (k, v) => setWForm((f) => ({ ...f, [k]: v }));

  const paidOut    = withdrawals.filter((w) => w.status === "Paid").reduce((s, w) => s + (w.amount || 0), 0);
  const pendingOut = withdrawals.filter((w) => w.status === "Pending").reduce((s, w) => s + (w.amount || 0), 0);
  const available  = Math.max(0, (data.totalEarnings || 0) - paidOut - pendingOut);

  const handleWithdrawRequest = async () => {
    const amount = parseFloat(wForm.amount);
    if (!wForm.phone.trim())          { setWError("Enter your mobile money number."); return; }
    if (isNaN(amount) || amount <= 0) { setWError("Enter a valid amount.");            return; }
    if (amount > available)           { setWError(`You can withdraw up to ${fmtMK(available)}.`); return; }

    setWSubmitting(true);
    setWError("");

    if (preview) {
      // Local-only, so clicking around the preview never writes real data.
      setWithdrawals((prev) => [{
        id: `preview-${Date.now()}`,
        referralCode: data.code,
        operator:     wForm.operator,
        phone:        wForm.phone.trim(),
        amount,
        status:       "Pending",
        createdAt:    new Date(),
      }, ...prev]);
      setWForm({ operator: "airtel", phone: "", amount: "" });
      setFormOpen(false);
      setWSubmitting(false);
      return;
    }

    await createReferralWithdrawal({
      referralCode: data.code,
      userId:       data.userId || null,
      operator:     wForm.operator,
      phone:        wForm.phone.trim(),
      amount,
      status:       "Pending",
    });
    setWForm({ operator: "airtel", phone: "", amount: "" });
    setFormOpen(false);
    loadWithdrawals();
    setWSubmitting(false);
  };

  return (
    <div className="profile-card">
      <div className="profile-card__header">
        <h2 className="profile-card__title">Referrals</h2>
        <span className="profile-card__count">{data.commissionRate}% commission</span>
      </div>

      {preview && (
        <div style={{
          background: "#fff8e1", border: "1px solid #ffe0a3", borderRadius: 8,
          padding: "8px 12px", marginBottom: 16, fontSize: 11.5, color: "#8a6d1f",
        }}>
          Preview mode — sample data, nothing here is saved.
        </div>
      )}

      <div className="profile-referral__link-box">
        <p className="profile-referral__link-label">Your referral link</p>
        <div className="profile-referral__link-row">
          <span className="profile-referral__link">{link}</span>
          <button className="profile-referral__copy-btn" onClick={copyLink}>
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>

      <div className="profile-referral__stats">
        <div className="profile-referral__stat">
          <span className="profile-referral__stat-value">{data.totalClicks || 0}</span>
          <span className="profile-referral__stat-label">Clicks</span>
        </div>
        <div className="profile-referral__stat">
          <span className="profile-referral__stat-value">{data.totalSignups || 0}</span>
          <span className="profile-referral__stat-label">Signups</span>
        </div>
        <div className="profile-referral__stat">
          <span className="profile-referral__stat-value">{data.totalOrders || 0}</span>
          <span className="profile-referral__stat-label">Orders</span>
        </div>
        <div className="profile-referral__stat">
          <span className="profile-referral__stat-value">{fmtMK(data.totalEarnings)}</span>
          <span className="profile-referral__stat-label">Earnings</span>
        </div>
      </div>

      <div className="profile-referral__commissions">
        <p className="profile-referral__commissions-title">Recent Commissions</p>
        {ordersLoading ? (
          <p className="profile-referral__commissions-empty">Loading…</p>
        ) : orders.length === 0 ? (
          <p className="profile-referral__commissions-empty">No referred orders yet.</p>
        ) : (
          <div className="profile-referral__commission-list">
            {orders.map((order) => {
              const earned    = ((order.total || 0) * (data.commissionRate || 0)) / 100;
              const itemsText = (order.items || [])
                .map((item) => `${item.name}${(item.qty || 1) > 1 ? ` ×${item.qty}` : ""}`)
                .join(", ");
              return (
                <div key={order.id} className="profile-referral__commission">
                  <div className="profile-referral__commission-left">
                    <p className="profile-referral__commission-date">{fmtDate(order.createdAt)}</p>
                    <p className="profile-referral__commission-items">{itemsText || "—"}</p>
                  </div>
                  <div className="profile-referral__commission-right">
                    <p className="profile-referral__commission-total">{fmtMK(order.total)}</p>
                    <p className="profile-referral__commission-earned">+{fmtMK(earned)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="profile-referral__balance-box">
        <div>
          <p className="profile-referral__link-label">Available to withdraw</p>
          <p className="profile-referral__stat-value" style={{ fontSize: 22 }}>{fmtMK(available)}</p>
        </div>
        <button
          className="profile-form__save"
          style={{ flex: "none", padding: "9px 16px" }}
          onClick={() => { setFormOpen((v) => !v); setWError(""); }}
          disabled={available <= 0 && !formOpen}
        >
          {formOpen ? "Cancel" : "Request Withdrawal"}
        </button>
      </div>

      {formOpen && (
        <div className="profile-form" style={{ marginTop: 12 }}>
          <div className="profile-form__group">
            <label className="profile-form__label">Operator</label>
            <select
              className="profile-form__input"
              value={wForm.operator}
              onChange={(e) => setW("operator", e.target.value)}
            >
              {MOBILE_OPERATORS.map((op) => (
                <option key={op.id} value={op.id}>{op.label}</option>
              ))}
            </select>
          </div>
          <div className="profile-form__group">
            <label className="profile-form__label">Mobile Money Number</label>
            <input
              className="profile-form__input"
              placeholder="e.g. 0990000000"
              value={wForm.phone}
              onChange={(e) => setW("phone", e.target.value)}
            />
          </div>
          <div className="profile-form__group">
            <label className="profile-form__label">Amount (MWK)</label>
            <input
              className="profile-form__input"
              type="number"
              min="1"
              placeholder={`Up to ${fmtMK(available)}`}
              value={wForm.amount}
              onChange={(e) => setW("amount", e.target.value)}
            />
          </div>
          {wError && <p className="profile-form__error">{wError}</p>}
          <div className="profile-form__actions">
            <button className="profile-form__save" onClick={handleWithdrawRequest} disabled={wSubmitting}>
              {wSubmitting ? "Submitting…" : "Submit Request"}
            </button>
          </div>
        </div>
      )}

      <div className="profile-referral__commissions">
        <p className="profile-referral__commissions-title">Withdrawal History</p>
        {wLoading ? (
          <p className="profile-referral__commissions-empty">Loading…</p>
        ) : withdrawals.length === 0 ? (
          <p className="profile-referral__commissions-empty">No withdrawal requests yet.</p>
        ) : (
          <div className="profile-referral__commission-list">
            {withdrawals.map((w) => (
              <div key={w.id} className="profile-referral__commission">
                <div className="profile-referral__commission-left">
                  <p className="profile-referral__commission-date">{fmtDate(w.createdAt)}</p>
                  <p className="profile-referral__commission-items">
                    {MOBILE_OPERATORS.find((o) => o.id === w.operator)?.label || w.operator} · {w.phone}
                  </p>
                </div>
                <div className="profile-referral__commission-right">
                  <p className="profile-referral__commission-total">{fmtMK(w.amount)}</p>
                  <span style={{
                    ...(WITHDRAWAL_STATUS_STYLE[w.status] || WITHDRAWAL_STATUS_STYLE.Pending),
                    fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "2px 8px", borderRadius: 20,
                  }}>
                    {w.status || "Pending"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="profile-referral__note">
        Approved requests are paid out manually to your chosen mobile money number, usually within 1–2 business days.
      </p>
    </div>
  );
}

// Shown when the owner previews "Referral Holder" mode via OwnerViewBar and
// isn't themselves tied to a real referral code — lets them see the withdrawal
// dashboard design without touching a real partner's data.
const SAMPLE_REFERRAL = {
  code: "SAMPLE10", commissionRate: 10, userId: null,
  totalClicks: 24, totalSignups: 6, totalOrders: 3, totalEarnings: 15000,
};

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function MyProfile({ cartCount, onCartClick }) {
  const navigate    = useNavigate();
  const { user, signOut, isOwner, viewAs } = useAuth();
  const isPhoneUser = user?.providerData?.[0]?.providerId === "password";
  const previewingReferral = isOwner && viewAs === "referral";
  const [activeTab,    setActiveTab]    = useState("orders");
  const [referralData, setReferralData] = useState(null);
  const [ownedEvents,  setOwnedEvents]  = useState([]);

  useEffect(() => {
    if (user === null) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    if (previewingReferral) { setReferralData(SAMPLE_REFERRAL); return; }
    getReferralByUserId(user.uid).then((data) => { if (data) setReferralData(data); });
  }, [user, previewingReferral]);

  useEffect(() => {
    if (!user) return;
    getEventsByOwner(user.uid).then(setOwnedEvents);
  }, [user]);

  useEffect(() => {
    if (previewingReferral) setActiveTab("referrals");
  }, [previewingReferral]);

  const handleSignOut = () => { signOut(); navigate("/"); };

  if (!user) return null;

  return (
    <div className="my-profile">
      <Navbar cartCount={cartCount} onCartClick={onCartClick} />

      <div className="my-profile__tabs">
        <button
          className={`my-profile__tab-btn${activeTab === "orders" ? " my-profile__tab-btn--active" : ""}`}
          onClick={() => setActiveTab("orders")}
        >
          <ChevronLeftIcon />
          <span>Orders</span>
        </button>
        <button
          className={`my-profile__tab-btn${activeTab === "tickets" ? " my-profile__tab-btn--active" : ""}`}
          onClick={() => setActiveTab("tickets")}
        >
          <span>Tickets</span>
        </button>
        {referralData && (
          <button
            className={`my-profile__tab-btn${activeTab === "referrals" ? " my-profile__tab-btn--active" : ""}`}
            onClick={() => setActiveTab("referrals")}
          >
            <span>Referrals</span>
          </button>
        )}
        {ownedEvents.length > 0 && (
          <button
            className={`my-profile__tab-btn${activeTab === "events" ? " my-profile__tab-btn--active" : ""}`}
            onClick={() => setActiveTab("events")}
          >
            <span>My Events</span>
          </button>
        )}
        <button
          className={`my-profile__tab-btn${activeTab === "details" ? " my-profile__tab-btn--active" : ""}`}
          onClick={() => setActiveTab("details")}
        >
          <span>Personal Details</span>
          <ChevronRightIcon />
        </button>
      </div>

      <div className="my-profile__main">
        <div className="my-profile__left-col">
          <div className={`my-profile__panel${activeTab === "orders" ? " my-profile__panel--active" : ""}`}>
            <OrdersCard userId={user.uid} />
          </div>
          <div className={`my-profile__panel${activeTab === "tickets" ? " my-profile__panel--active" : ""}`}>
            <TicketsCard userId={user.uid} />
          </div>
        </div>
        <div className="my-profile__right-col">
          <div className={`my-profile__panel${activeTab === "details" ? " my-profile__panel--active" : ""}`}>
            <PersonalCard user={user} isPhoneUser={isPhoneUser} onSignOut={handleSignOut} />
          </div>
          {referralData && (
            <div className={`my-profile__panel${activeTab === "referrals" ? " my-profile__panel--active" : ""}`}>
              <ReferralsCard data={referralData} preview={previewingReferral} />
            </div>
          )}
          {ownedEvents.length > 0 && (
            <div className={`my-profile__panel${activeTab === "events" ? " my-profile__panel--active" : ""}`}>
              <EventOwnerCard userId={user.uid} ownerName={user.displayName || user.email || ""} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
